'use client';

import { API_ORIGIN } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { Database, Loader2, MessageCircle, Send, Sparkles, X } from 'lucide-react';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const HELP_SUGGESTIONS = [
  'How do I publish a dashboard?',
  'How do scheduled reports work?',
  'What does CPL mean?',
  'How do I invite a teammate?',
];

const DATA_SUGGESTIONS = [
  'What changed this week?',
  'Top model by leads',
  'Spend vs last week',
  'Which campaign has highest CPL?',
];

const GREETING_HELP: ChatMessage = {
  role: 'assistant',
  content:
    "Hey — I'm the Adholics assistant. Ask me anything about features, workflows, or how to get something done.",
};
const GREETING_DATA = (region: string): ChatMessage => ({
  role: 'assistant',
  content: `Data mode on for **${region}**. Ask about your latest pacing, top campaigns, what changed week-over-week, etc. I'll only use the most recent snapshot.`,
});

/**
 * In-app chat widget. Mirrors the marketing-site widget but runs with
 * `context: 'app'` so the bot knows the user is signed in.
 *
 * The component is mounted once at the app root (App.tsx) — the launcher
 * is fixed-bottom-right and stays available across every authenticated
 * route.
 */
export function ChatbotWidget() {
  const { getAccessToken, sessionKey, me } = useAuth();
  const [open, setOpen] = useState(false);
  const [dataRegion, setDataRegion] = useState<string | null>(null); // null = help mode
  const [messages, setMessages] = useState<ChatMessage[]>([GREETING_HELP]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Available regions for the active brand (read from brand settings if
  // exposed, otherwise common defaults). The /chat endpoint enforces
  // ai_insights gating so we don't gate on the client.
  const availableRegions = useMemo<string[]>(() => {
    // me?.brands doesn't carry settings yet; show a common default list
    // until we surface it through /auth/me. Users can type their own
    // region in the composer if it's not here.
    return ['NDL', 'BE', 'NO', 'FR', 'DE'];
  }, []);

  const switchMode = (region: string | null) => {
    setDataRegion(region);
    setMessages(region ? [GREETING_DATA(region)] : [GREETING_HELP]);
    setError(null);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  const send = async (text: string) => {
    if (!text.trim() || busy) return;
    const next = [...messages, { role: 'user' as const, content: text.trim() }];
    setMessages(next);
    setInput('');
    setBusy(true);
    setError(null);
    try {
      // Strip out any greeting message — those are UI-only, never sent
      // to the API (they'd confuse the conversation history).
      const sendMessages = next.filter(
        (m) =>
          !(
            m.role === 'assistant' &&
            (m.content === GREETING_HELP.content || m.content.startsWith('Data mode on'))
          ),
      );
      const headers: Record<string, string> = { 'content-type': 'application/json' };
      if (dataRegion && sessionKey) {
        const accessToken = getAccessToken();
        if (accessToken) headers.authorization = `Bearer ${accessToken}`;
      }
      const res = await fetch(`${API_ORIGIN}/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          messages: sendMessages,
          context: 'app',
          dataContext: dataRegion ? { country: dataRegion } : null,
        }),
      });
      if (!res.ok) throw new Error('http_error');
      const data = (await res.json()) as { content: string; dataMode?: boolean };
      setMessages((m) => [...m, { role: 'assistant', content: data.content }]);
      if (dataRegion && data.dataMode === false) {
        // Asked for data mode but server fell back (no snapshot or no
        // ai_insights). Tell the user once.
        setMessages((m) => [
          ...m,
          {
            role: 'assistant',
            content:
              "(Heads up — I don't have data for that region yet, so I answered from product knowledge. Make sure the workspace has data sources connected and the cron has run at least once.)",
          },
        ]);
      }
    } catch {
      setError("I couldn't reach the assistant — try again, or email support@adholics.net.");
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void send(input);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full px-4 py-3 text-sm font-semibold shadow-[0_8px_24px_-6px_rgba(0,0,0,0.5)] transition',
          open ? 'bg-surface text-fg ring-1 ring-line' : 'bg-brand text-white hover:bg-brand-hover',
        )}
        aria-expanded={open}
        aria-label={open ? 'Close chat' : 'Open chat'}
      >
        {open ? (
          <>
            <X className="size-4" /> Close
          </>
        ) : (
          <>
            <MessageCircle className="size-4" /> Ask
          </>
        )}
      </button>

      {open ? (
        <aside
          role="dialog"
          aria-label="Adholics assistant"
          className="fixed bottom-20 right-5 z-40 flex h-[500px] w-[360px] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-[0_24px_64px_-12px_rgba(0,0,0,0.6)]"
        >
          <header className="space-y-2 border-b border-line bg-surface-2 px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="flex size-7 items-center justify-center rounded-full bg-brand text-white">
                <Sparkles className="size-3.5" />
              </div>
              <div className="space-y-0.5">
                <p className="text-sm font-semibold text-fg">Adholics assistant</p>
                <p className="text-[11px] text-muted-foreground">
                  {dataRegion ? `Data mode · ${dataRegion}` : 'Help mode'}
                </p>
              </div>
            </div>
            {me?.user ? (
              <div className="flex flex-wrap items-center gap-1 rounded-md bg-bg p-0.5">
                <button
                  type="button"
                  onClick={() => switchMode(null)}
                  className={cn(
                    'rounded px-2 py-1 text-[11px] font-medium transition',
                    dataRegion === null
                      ? 'bg-surface text-fg shadow-sm'
                      : 'bg-transparent text-muted-foreground hover:text-fg',
                  )}
                >
                  Help
                </button>
                {availableRegions.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => switchMode(r)}
                    className={cn(
                      'inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium transition',
                      dataRegion === r
                        ? 'bg-surface text-fg shadow-sm'
                        : 'bg-transparent text-muted-foreground hover:text-fg',
                    )}
                    title={`Ask about ${r} data`}
                  >
                    <Database className="size-3" />
                    {r}
                  </button>
                ))}
              </div>
            ) : null}
          </header>
          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-3 text-sm">
            {messages.map((m, i) => (
              <Bubble key={i} role={m.role}>
                {m.content}
              </Bubble>
            ))}
            {busy ? (
              <Bubble role="assistant">
                <Loader2 className="size-3.5 animate-spin" /> Thinking…
              </Bubble>
            ) : null}
            {error ? <p className="text-xs text-danger">{error}</p> : null}
            {messages.length === 1 && !busy ? (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {(dataRegion ? DATA_SUGGESTIONS : HELP_SUGGESTIONS).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => void send(s)}
                    className="rounded-full border border-line bg-surface-2 px-2.5 py-1 text-[11px] text-muted-foreground hover:border-brand hover:text-fg"
                  >
                    {s}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <form
            onSubmit={onSubmit}
            className="flex items-end gap-2 border-t border-line bg-surface-2 px-3 py-3"
          >
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send(input);
                }
              }}
              placeholder="Ask about features, workflows, or how to do something…"
              rows={1}
              className="flex-1 resize-none rounded-md border border-line bg-bg px-3 py-2 text-sm text-fg placeholder:text-muted-foreground focus:border-brand focus:outline-none"
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              className="inline-flex size-9 items-center justify-center rounded-md bg-brand text-white disabled:opacity-50"
              aria-label="Send"
            >
              <Send className="size-4" />
            </button>
          </form>
        </aside>
      ) : null}
    </>
  );
}

function Bubble({ role, children }: { role: 'user' | 'assistant'; children: React.ReactNode }) {
  return (
    <div className={cn('flex', role === 'user' ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'inline-flex max-w-[85%] gap-1.5 rounded-2xl px-3 py-2',
          role === 'user' ? 'bg-brand text-white' : 'bg-bg text-fg-soft border border-line',
        )}
      >
        <div className="text-sm leading-snug whitespace-pre-wrap">{children}</div>
      </div>
    </div>
  );
}
